// Deep-link card primitive: the only way out to another app. CRM,
// operations, HR/payroll, organization administration in SYLVIQ Console, the
// document store, and the customer portal never render inside accounting —
// they render as a card that links out with the organization scope attached.
// When the target origin is unconfigured the card renders disabled with an
// explanation, never a dead link.
import {
  Card,
  CardContent,
  CardDescription,
  CardTitle,
} from "@sylviq/ui-react-web/components/ui/card";
import {
  buildDeepLink,
  type CrossAppTarget,
  type DeepLinkScope,
} from "@/lib/links";

interface DeepLinkCardProps {
  target: CrossAppTarget;
  /** Path inside the target app, e.g. `/contacts/abc`. */
  path?: string;
  scope?: DeepLinkScope;
  /** Override the default plain-language copy for this usage. */
  title?: string;
  body?: string;
}

const PLATFORM_ADMIN_COPY = {
  label: "Organization administration",
  detail: "Members, roles, and billing live in SYLVIQ Console.",
};

export function DeepLinkCard({
  target,
  path,
  scope,
  title,
  body,
}: DeepLinkCardProps) {
  const defaultCopy = target === "platformAdmin" ? PLATFORM_ADMIN_COPY : null;
  const link = buildDeepLink(target, {
    path,
    scope,
    label: title ?? defaultCopy?.label,
    detail: body ?? defaultCopy?.detail,
  });

  if (!link.configured || !link.href) {
    return (
      <Card aria-disabled="true">
        <CardContent className="grid gap-1 p-5">
          <CardTitle>{link.label}</CardTitle>
          <CardDescription>{link.detail}</CardDescription>
          <p className="mt-2 text-xs text-[var(--muted-foreground)]">
            {link.unavailableReason}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <a
      href={link.href}
      className="block rounded-2xl transition hover:-translate-y-px"
    >
      <Card>
        <CardContent className="grid gap-1 p-5">
          <CardTitle>{link.label}</CardTitle>
          <CardDescription>{link.detail}</CardDescription>
          <span className="mt-2 text-xs font-medium text-[var(--muted-foreground)]">
            Open in {targetName(target)} →
          </span>
        </CardContent>
      </Card>
    </a>
  );
}

function targetName(target: CrossAppTarget): string {
  switch (target) {
    case "crm":
      return "CRM";
    case "ops":
      return "operations";
    case "hrPayroll":
      return "HR and payroll";
    case "platformAdmin":
      return "SYLVIQ Console";
    case "documents":
      return "the document store";
    case "portal":
      return "the customer portal";
  }
}
